import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../../components/ui/button";

export interface SimpleBreakdownPaginationProps {
  currentPage: number;
  totalPages: number;
  totalElements: number;
  pageSize?: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
  itemLabel?: string;
}

export function SimpleBreakdownPagination({
  currentPage,
  totalPages,
  totalElements,
  pageSize = 50,
  loading = false,
  onPageChange,
  itemLabel = "groups",
}: SimpleBreakdownPaginationProps) {
  if (totalElements <= 0) return null;

  const start = currentPage * pageSize + 1;
  const end = Math.min((currentPage + 1) * pageSize, totalElements);

  const getPages = (): (number | "dots")[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 3) {
      return [1, 2, 3, 4, 5, "dots", totalPages];
    }
    if (currentPage >= totalPages - 4) {
      return [1, "dots", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, "dots", currentPage, currentPage + 1, currentPage + 2, "dots", totalPages];
  };

  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 pt-3 text-xs text-muted-foreground">
      <div>
        Showing <span className="font-semibold text-foreground">{start}</span> to{" "}
        <span className="font-semibold text-foreground">{end}</span> of{" "}
        <span className="font-semibold text-foreground">{totalElements.toLocaleString()}</span> {itemLabel}
      </div>

      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={currentPage <= 0 || loading}
          onClick={() => onPageChange(currentPage - 1)}
          className="flex h-7 items-center gap-1 px-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-transparent disabled:cursor-not-allowed disabled:opacity-40 transition-colors"
        >
          <ChevronLeft className="size-3.5" />
          Previous
        </Button>

        {getPages().map((p, idx) =>
          p === "dots" ? (
            <span key={`dots-${idx}`} className="px-1 text-muted-foreground select-none">
              …
            </span>
          ) : (
            <Button
              key={p}
              type="button"
              variant={p - 1 === currentPage ? "default" : "ghost"}
              size="sm"
              disabled={loading}
              onClick={() => onPageChange(p - 1)}
              className={`flex size-7 items-center justify-center rounded-md text-xs font-semibold p-0 transition-colors ${
                p - 1 === currentPage
                  ? "bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 hover:text-white"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              }`}
            >
              {p}
            </Button>
          ),
        )}

        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={currentPage >= totalPages - 1 || loading}
          onClick={() => onPageChange(currentPage + 1)}
          className="flex h-7 items-center gap-1 px-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-transparent disabled:cursor-not-allowed disabled:opacity-40 transition-colors"
        >
          Next
          <ChevronRight className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}
